import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BetaForm as useBetaForm } from "@reusejs/react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger,
  DialogDescription,
} from "../../../../../shadcn/components/ui/dialog";
import { Input } from "../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../shadcn/components/ui/button";
import { Dropdown } from "../../../../../shadcn/components/custom/Dropdown";
import { Label } from "../../../../../shadcn/components/ui/label";
import CustomMultipleFileUploader from "../../../../banker/bidder/CustomMultipleFileUploader";
import CalendarDotsIcon from "../../../../../icons/CalendarDotsIcon";
import { Calendar } from "../../../../../shadcn/components/ui/calendar";
import { getBidderByUuid, updateBidderForEvent } from "services";
import { requestDDValidation } from "services/bidder";

interface PaymentValidationApplicationFormProps {
  bidderUuid: any;
  init: any;
  bidder: any;
  activeStep: any;
  setActiveStep: any;
}

export default function PaymentValidationApplicationForm({
  bidderUuid,
  init,
  bidder,
  activeStep,
  setActiveStep,
}: PaymentValidationApplicationFormProps) {
  console.log("bidder: ", bidder);
  const [loading, setLoading] = useState(false);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const emdPaymentMode = [
    { label: "Online Transfer", value: "online" },
    { label: "DD/PO Submitted", value: "dd" },
  ];
  const form = useBetaForm({
    payment_mode: {
      label: "DD/PO Submitted",
      value: "dd",
    },
    approver_email: "",
    approver_mobile: "",
    approver_name: "",
    transaction_amount: "",
    transaction_id: "",
    emd_proof_uuid: "",
    emd_paid_date: "",
    emd_paid_from: "",
  });
  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
  const form_constraints = {
    approver_mobile: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    approver_email: {
      presence: {
        allowEmpty: false,
      },
      email: true,
    },
    approver_name: {
      presence: {
        allowEmpty: false,
      },
    },
    transaction_amount: {
      presence: { allowEmpty: false },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo:
          typeof bidder?.meta?.emd_amount === "string"
            ? parseInt(bidder?.meta?.emd_amount)
            : bidder?.meta?.emd_amount,
        message: `^Transaction amount must be greater than or equal to EMD amount:${bidder?.meta?.emd_amount}`,
      },
    },
    transaction_id: {
      presence: { allowEmpty: false },
    },
    emd_proof_uuid: {
      presence: { allowEmpty: false },
    },
    emd_paid_date: {
      presence: { allowEmpty: false },
    },
    emd_paid_from: {
      presence: { allowEmpty: false },
    },
    payment_mode: {
      presence: { allowEmpty: false },
    },
  };
  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form?.value]);

  const handleSubmit = async () => {
    form.setField("emd_proof_uuid", kycFiles);
    console.log("handleSubmit-----", form.validate(), form.value, form.errors);
    if (form.validate()) {
      setLoading(true);
      const data: any = await getBidderByUuid(bidderUuid);
      console.log("form?.value---", form?.value);
      await updateBidderForEvent({
        uuid: bidderUuid,
        payment_data: {
          ...data?.payment_data,
          ...form?.value,
          form_submission: "submitted",
          approval_status: "submitted",
        },
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const payload = {
        dd_file_uuid: kycFiles?.[0]?.id,
        email: form.getField("approver_email"),
        event_uuid: bidder?.event_uuid,
        bidder_uuid: bidderUuid,
      };
      await requestDDValidation(payload);
      setActiveStep(activeStep + 1);
      await init();
      setLoading(false);
    }
  };
  return (
    <div>
      {bidder?.payment_data?.form_submission === "submitted" ? (
        <div className="text-sm">This step is completed.</div>
      ) : (
        <div>
          <h4 className="font-bold text-lg font-openSans">
            EMD Payment Validation
          </h4>
          <div className="text-xs md:text-sm text-brand-textGrey mt-2 md:mt-4 mb-4 md:mb-8">
            <strong className="text-brand-textBlack">Property:</strong>{" "}
            {bidder?.meta?.event_name}
          </div>
          <div className="font-semibold">EMD Payment Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="payment_mode">Payment Mode*</Label>
              <Dropdown
                disabled={true}
                dataSource={() => {
                  return Promise.resolve(emdPaymentMode);
                }}
                defaultSelected={
                  form.getField("payment_mode") !== ""
                    ? form.getField("payment_mode")
                    : null
                }
                selectIdentifier={true}
                onChange={async (value: any) => {
                  form.setField("payment_mode", value);
                }}
                valueKey="value"
                placeholder="Select Payment Mode*"
                styles={{
                  hideDot: true,
                  dropdownItemsClass: "text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightPink",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  outerContainer:
                    "border-[1px] border-brand-borderLightGray rounded-sm text-xs sm:text-sm leading-normal font-sans text-brand-textGrey px-2 sm:px-4 h-[38px]",
                }}
              />
              {form.errors.get("payment_mode") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("payment_mode")[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="email">Payment Receiver Email*</Label>
              <Input
                placeholder="Enter Email of Bank Officer Receiver*"
                value={form.getField("approver_email")}
                onChange={(e: any) => {
                  form.setField("approver_email", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_email") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_email")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="mobile">Payment Receiver Mobile*</Label>
              <Input
                type="number"
                placeholder={"Payment Receiver Mobile*"}
                value={form.getField("approver_mobile")}
                onChange={(e: any) => {
                  if (e.target.value >= 0 || e.target.value == "") {
                    form.setField("approver_mobile", e?.target?.value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_mobile") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_mobile")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="name">
                Payment Receiver Name*
              </Label>
              <Input
                placeholder="Payment Receiver Name*"
                value={form.getField("approver_name")}
                onChange={(e: any) => {
                  form.setField("approver_name", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_name") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_name")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label
                className="text-brand-bgBlack"
                htmlFor="transaction_amount"
              >
                Transaction Amount*
              </Label>
              <Input
                type="number"
                placeholder={"Transaction Amount*"}
                value={form.getField("transaction_amount")}
                onChange={(e: any) => {
                  const rawValue = e?.target?.value;
                  if (rawValue === "") {
                    form.setField("transaction_amount", "");
                  } else {
                    const numericValue = Number(rawValue);
                    const value = isNaN(numericValue)
                      ? ""
                      : Math.abs(numericValue);
                    form.setField("transaction_amount", value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("transaction_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("transaction_amount")?.[0]}
                </p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="transaction_id">
                {" "}
                Transaction Id or DD Number*
              </Label>
              <Input
                type="number"
                placeholder={"Transaction Id*"}
                value={form.getField("transaction_id")}
                onChange={(e: any) => {
                  const rawValue = e?.target?.value;
                  if (rawValue === "") {
                    form.setField("transaction_id", "");
                  } else {
                    const numericValue = Number(rawValue);
                    const value = isNaN(numericValue)
                      ? ""
                      : Math.abs(numericValue);
                    form.setField("transaction_id", value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("transaction_id") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("transaction_id")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="emd_paid_date">EMD Paid Date*</Label>
              <DateCard form={form} />
              {form.errors.get("emd_paid_date") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_paid_date")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="emd_paid_from">
                Bank Name Where Payment Received*
              </Label>
              <Input
                placeholder="Bank Name Where Payment Received*"
                type="text"
                value={form.getField("emd_paid_from")}
                onChange={(e: any) => {
                  const textOnly = e.target.value.replace(/[0-9]/g, "");
                  form.setField("emd_paid_from", textOnly);
                  // form.setField("emd_paid_from", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("emd_paid_from") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_paid_from")?.[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="py-8 font-semibold">EMD Payment Proof</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div className="col-span-1 md:col-span-2">
              <CustomMultipleFileUploader
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["image/png", "image/jpeg"]}
                property_uuid={bidderUuid}
                owner_service_type="emd-payment-slip"
                data={kycFiles}
                defaultText="Please upload proof of your EMD payment"
              />
              {form.errors.get("emd_proof_uuid") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_proof_uuid")[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-8 mb-10 md:mb-0 flex justify-center">
            <Button
              disabled={loading}
              className="w-full max-w-xl h-10 text-[18px]"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
      {loading && <LoadingModal />}
    </div>
  );
}

interface DateCardProps {
  form: any;
}
const DateCard = ({ form }: DateCardProps) => {
  // const { searchForm }: any = useContext(Context);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);

  const formatDate = (date: Date | undefined): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear().toString();
    return `${day}/${month}/${year}`;
  };

  const handleDateSelect = (date: Date | undefined) => {
    setStartDate(date);
    form.setField("emd_paid_date", date || "");
    setOpen(false);
  };

  return (
    <div className="">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="flex gap-2 items-center w-full justify-between text-sm font-openSans text-brand-textLightGrey"
          >
            {startDate ? (
              formatDate(startDate)
            ) : (
              <span>Select EMD paid Date</span>
            )}
            <CalendarDotsIcon width={16} height={16} />
          </Button>
        </DialogTrigger>
        <DialogContent removeIcon={true} className="max-w-[340px] rounded-lg">
          <DialogTitle className="hidden"> </DialogTitle>
          <DialogDescription className="hidden"></DialogDescription>
          <div className="font-openSans text-lg font-semibold text-brand-textLightGrey px-3">
            Select EMD paid Date
          </div>
          <Calendar
            mode="single"
            selected={startDate}
            onSelect={handleDateSelect}
            fromDate={new Date(new Date().setMonth(new Date().getMonth() - 2))}
            toDate={new Date()}
            initialFocus
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface LoadingModalProps {
  loadingText?: string;
}

const LoadingModal = ({ loadingText }: LoadingModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className=" text-white flex flex-col relative">
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
          <div className="mt-2 text-sm font-openSans text-center">
            {loadingText || "Saving Details. Please wait"}
          </div>
        </div>
      </div>
    </div>
  );
};
